import useSWRInfinite from 'swr/infinite';
import { mutate as globalMutate } from 'swr';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';

export interface Invoice {
  id: string;
  number: string;
  customer_id: string;
  issue_date: string;
  due_date: string;
  status: string;
  sub_total: number;
  tax_total: number;
  balance_due: number;
  terms?: string;
  customer?: Customer;
  item_count?: number;
  total_amount?: number;
  // Add customer display fields for client-side filtering
  display_name?: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  billing_addr_line1?: string;
  billing_addr_city?: string;
  billing_addr_state?: string;
  billing_addr_zip?: string;
}

interface Customer {
  id: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  billing_addr_line1?: string;
  billing_addr_city?: string;
  billing_addr_state?: string;
  billing_addr_zip?: string;
}

interface InvoicesResponse {
  invoices: Invoice[];
  nextCursor: string | null;
}

const PAGE_SIZE = 300;
const SEARCH_DEBOUNCE_MS = 300; // 300ms delay for server search

export const useInvoicesInfinite = (
  searchTerm?: string,
  statusFilter?: string,
  customerId?: string
) => {
  const { token, currentBusiness, isCookieMode, API_BASE_URL } = useAuth();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm || '');
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);

  // Debounce search term to prevent rapid API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm || '');
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getKey = (pageIndex: number, previousPageData: InvoicesResponse | null) => {
    // Reached the end
    if (previousPageData && !previousPageData.nextCursor) return null;

    // First page, we don't have `previousPageData`
    if (pageIndex === 0) {
      const params = new URLSearchParams();
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (customerId) params.append('customer_id', customerId);
      params.append('limit', PAGE_SIZE.toString());
      
      return ['/invoices', currentBusiness?.id, params.toString()];
    }

    // Add the cursor to the API endpoint
    const params = new URLSearchParams();
    if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
    if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
    if (customerId) params.append('customer_id', customerId);
    params.append('after', previousPageData!.nextCursor!);
    params.append('limit', PAGE_SIZE.toString());

    return ['/invoices', currentBusiness?.id, params.toString()];
  };

  const fetcher = async ([url, businessId, params]: [string, string, string]) => {
    if (!currentBusiness) {
      throw new Error('Business context required');
    }
    
    if (!isCookieMode && !token) {
      throw new Error('Authentication required');
    }

    const headers: any = {
    };
    
    // Only add Authorization header if not in cookie mode
    if (!isCookieMode && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Use the environment variable to construct the full API URL
    const fullUrl = `${API_BASE_URL}${url}?${params}`;
    
    const response = await axios.get(fullUrl, {
      headers,
      withCredentials: isCookieMode, // This sends cookies with the request
    });

    return response.data;
  };

  const {
    data,
    error,
    isLoading,
    isValidating,
    size,
    setSize,
    mutate,
  } = useSWRInfinite<InvoicesResponse>(getKey, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 0, // Disable deduping to ensure immediate updates
  });

  // Clear cache and revalidate immediately when business changes
  useEffect(() => {
    if (currentBusiness?.id) {
      // Clear the local state immediately
      setAllInvoices([]);
      // Reset pagination to first page
      setSize(1);
      // Clear all SWR caches to ensure fresh data
      globalMutate(() => true, undefined, { revalidate: false });
      // Force revalidation of all cached data with aggressive cache clearing
      mutate(undefined, { revalidate: true });
    }
  }, [currentBusiness?.id, mutate, setSize]);

  // Store all fetched invoices for client-side filtering
  useEffect(() => {
    if (data) {
      const allFetched = data.flatMap((page: InvoicesResponse) => page.invoices);
      setAllInvoices(allFetched);
    }
  }, [data]);

  // Client-side filtering for immediate results
  const filteredInvoices = useCallback(() => {
    if (!searchTerm || !allInvoices.length) return allInvoices;
    
    const searchLower = searchTerm.toLowerCase();
    return allInvoices.filter(invoice => {
      // Search by invoice number
      if (invoice.number.toLowerCase().includes(searchLower)) return true;
      
      // Search by customer name
      const customerName = (
        invoice.display_name || 
        `${invoice.first_name || ''} ${invoice.last_name || ''}`.trim() || 
        invoice.company_name || ''
      ).toLowerCase();
      if (customerName.includes(searchLower)) return true;
      
      // Search by address
      const address = `${invoice.billing_addr_line1 || ''} ${invoice.billing_addr_city || ''} ${invoice.billing_addr_state || ''} ${invoice.billing_addr_zip || ''}`.toLowerCase();
      if (address.includes(searchLower)) return true;
      
      return false;
    });
  }, [allInvoices, searchTerm]);

  // Use client-side filtered results for immediate display
  const invoices = filteredInvoices();
  
  const isLoadingMore = isLoading || (size > 0 && data && typeof data[size - 1] === "undefined");
  const isEmpty = data?.[0]?.invoices?.length === 0;
  const isReachingEnd = isEmpty || (data && data[data.length - 1]?.nextCursor === null);
  const isRefreshing = isValidating && data && data.length === size;

  const loadMore = () => {
    if (!isLoadingMore && !isReachingEnd) {
      setSize(size + 1);
    }
  };

  return {
    invoices,
    error,
    isLoading,
    isLoadingMore,
    isReachingEnd,
    isRefreshing,
    loadMore,
    mutate,
    isEmpty,
    // Add these for debugging and state management
    searchTerm,
    debouncedSearchTerm,
    allInvoicesCount: allInvoices.length,
    filteredInvoicesCount: invoices.length,
  };
}; 